/** Base class for floor-plan shapes; concrete subclasses live in `model/shapes/`. */

import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import type { UppKonvaDestroy } from './upp-konva-destroy';
import type { UppKonvaAttention } from './upp-konva-types';
import type { UppKonvaShapeKind } from './upp-konva-types';
import type { UppKonvaArea } from './upp-konva-area';
import type { UppKonvaStage } from '../stage/upp-konva-stage';
import type { UppKonvaShapeBaseInit } from './upp-konva-options';
import { newUppKonvaShapeId } from './upp-konva-shape-id';

export type { UppKonvaShapeKind };

/**
 * Minimal payload on `onSelected$` / `onUpdated$`. Not part of the persisted plan JSON.
 */
export type UppKonvaShapeEventPayload = {
    /** Opaque bag (for example echo of wire `custom`); the stage may forward it when applicable. */
    readonly custom?: unknown;
    /**
     * Only for **BAR** row clicks: global stool index (1…N, same order as on canvas);
     * **`null`** when the click hit the bar body, not a stool.
     * Absent for non-bar shapes.
     */
    readonly stool?: number | null;
};

/**
 * Live shape; each subclass fixes `kind`. Instances live in `UppKonvaArea.shapes` (same references on canvas and model).
 * `doDestroy` completes `onSelected$`, `onUpdated$`, and `onRemoved$`.
 */
export abstract class UppKonvaShape implements UppKonvaDestroy {
    // --------------------------------------------------------------------------
    // 1. Lifecycle
    // --------------------------------------------------------------------------

    /**
     * @param init - Geometry, labels, per-kind `data`, and wire metadata. When `id` is missing, a v4 UUID is assigned.
     */
    protected constructor(init: UppKonvaShapeBaseInit) {
        this.id = init.id ?? newUppKonvaShapeId();
        this.x = init.x;
        this.y = init.y;
        this.width = init.width;
        this.height = init.height;
        this.rotation = init.rotation;
        this.label = init.label;
        this.sublabel = init.sublabel;
        this.data = { ...init.data };
        if (init.zIndex !== undefined) {
            this.zIndex = init.zIndex;
        }
        if (init.mode !== undefined) {
            this.mode = init.mode;
        }
        if (init.custom !== undefined) {
            this.custom = init.custom;
        }
    }

    /** Completes shape observables; idempotent. */
    doDestroy(): void {
        if (this._destroyed) {
            return;
        }
        this._destroyed = true;
        this._parentArea = null;
        this._konvaStage = null;
        this._onSelected$.complete();
        this._onUpdated$.complete();
        this._onRemoved$.complete();
    }

    // --------------------------------------------------------------------------
    // 2. Properties & observables (integrator)
    // --------------------------------------------------------------------------

    abstract readonly kind: UppKonvaShapeKind;

    private readonly _onSelected$ = new Subject<UppKonvaShapeEventPayload>();
    private readonly _onUpdated$ = new Subject<UppKonvaShapeEventPayload>();
    private readonly _onRemoved$ = new Subject<void>();

    /**
     * Fires when the user selects the shape on the canvas while the model is in `VIEW`. Subscribe from the app.
     */
    readonly onSelected$: Observable<UppKonvaShapeEventPayload> = this._onSelected$.asObservable();

    /**
     * Fires when the shape is edited in place in `EDIT`. Does not imply a structural `area.shapes` change or `onModelChanged`.
     */
    readonly onUpdated$: Observable<UppKonvaShapeEventPayload> = this._onUpdated$.asObservable();

    /**
     * Fires once when the shape is removed via {@link UppKonvaArea.removeShapes} / {@link UppKonvaShape.remove}, synchronously
     * before `parentArea` is cleared and before `doDestroy`. Does **not** fire when the instance is torn down because `source`
     * was replaced (hydration / adapter redraw).
     */
    readonly onRemoved$: Observable<void> = this._onRemoved$.asObservable();

    readonly id: string;

    /** X position in cm (plan contract). */
    x: number;
    /** Y position in cm (plan contract). */
    y: number;
    /** Width in cm (plan contract). */
    width: number;
    /** Height in cm (plan contract). */
    height: number;
    /** Rotation in degrees (plan contract). */
    rotation: number;

    /**
     * JSON `data` fields per shape kind (chairs, door variant, bar stools, etc.).
     * Hydrated from the row DTO (`UppKonvaShapeSource.data`); cm geometry syncs with the canvas in edit mode.
     */
    data: Record<string, unknown>;

    /**
     * Primary label on the canvas (empty = no text). After in-place edits use `syncLabelsToStage` or `notifyPresentationDirty` on the area.
     */
    label: string;

    /**
     * Optional subtitle (typically smaller than `label`). See `syncLabelsToStage`.
     */
    sublabel: string;

    /** Z order in document / wire (optional). */
    zIndex?: number;
    /** Per-shape mode on wire (optional). */
    mode?: string;
    /** Opaque bag on wire (optional). */
    custom?: unknown;

    /** Owning area for this row in `shapes`, or `null` when not wired or after `doDestroy`. */
    get parentArea(): UppKonvaArea | null {
        return this._parentArea;
    }

    /**
     * English comment normalized.
     * Compatibility: assigning `true` is equivalent to {@link setHighlight}(`'WARNING'`, `true`); `false` to {@link clearHighlight}.
     * Not part of the persisted DTO `UppKonvaShapeSource`.
     */
    get attention(): boolean {
        if (this._destroyed) {
            return false;
        }
        return this._konvaStage?.shapeAttention(this.id) ?? false;
    }

    set attention(enabled: boolean) {
        if (this._destroyed) {
            return;
        }
        this._konvaStage?.setShapeAttention(this.id, enabled);
    }

    /**
     * Highlight state with tone and pulse; **null** if no highlight is active (applies only to **TABLE** on canvas).
     */
    get highlight(): { tone: UppKonvaAttention; animated: boolean } | null {
        if (this._destroyed) {
            return null;
        }
        return this._konvaStage?.shapeHighlightState(this.id) ?? null;
    }

    /**
     * Enables highlight only if the row is a **table**; does nothing for other types.
     *
     * @param tone — **SUCCESS**, **WARNING** or **DANGER** (floor-plan theme palette).
     * English comment normalized.
     */
    setHighlight(tone: UppKonvaAttention, animated: boolean): void {
        if (this._destroyed) {
            return;
        }
        this._konvaStage?.setShapeHighlight(this.id, tone, animated);
    }

    /** Clears the highlight state (same as {@link setHighlight} reset). */
    clearHighlight(): void {
        if (this._destroyed) {
            return;
        }
        this._konvaStage?.clearShapeHighlight(this.id);
    }

    /** Assigns the secondary badge text and syncs it with the wired stage. */
    setSublabel(text: string): void {
        this.sublabel = text ?? '';
        this.syncLabelsToStage();
    }

    // --------------------------------------------------------------------------
    // 3. Public methods (integrator)
    // --------------------------------------------------------------------------

    /**
     * After mutating `data` in place (visual variant, chairs, stools, …), applies presentation on the canvas without
     * changing global geometry. Requires a wired area and stage. Repaint is coalesced on the wired area (package-internal).
     * For x/y/dimensions use `area.notifyPresentationDirty()` or `area.source = …`.
     */
    syncPresentationToStage(): void {
        if (this._destroyed || !this._konvaStage) {
            return;
        }
        this._konvaStage.applyPresentationFromPublicShape({
            id: this.id,
            kind: this.kind,
            data: this.data,
        });
        this.notifyUpdated();
        this._parentArea?.requestRepaint();
    }

    /** After mutating `label` / `sublabel` in place, updates the scene node and canvas badge. */
    syncLabelsToStage(): void {
        if (this._destroyed || !this._konvaStage) {
            return;
        }
        this._konvaStage.applyLabelsFromPublicShape(this.id, this.label, this.sublabel);
        this.notifyUpdated();
    }

    /**
     * Removes this shape from the area and destroys it.
     *
     * @returns `false` without an owning area or when already destroyed.
     */
    remove(): boolean {
        if (this._destroyed || !this._parentArea) {
            return false;
        }
        return this._parentArea.removeShape(this);
    }

    // --------------------------------------------------------------------------
    // 4. Public methods — package internal (@internal)
    // --------------------------------------------------------------------------

    /**
     * @param area - Owning area, or `null` when detaching.
     * @internal
     */
    bindParentArea(area: UppKonvaArea | null): void {
        this._parentArea = area;
    }

    /**
     * @param stage - Konva stage bridge, or `null` when detaching.
     * @internal
     */
    bindKonvaStage(stage: UppKonvaStage | null): void {
        this._konvaStage = stage;
    }

    /**
     * @param payload - Selection payload; defaults to empty object.
     * @internal
     */
    notifySelected(payload: UppKonvaShapeEventPayload = {}): void {
        if (this._destroyed) {
            return;
        }
        this._onSelected$.next(payload);
    }

    /**
     * @param payload - Update payload; defaults to empty object.
     * @internal
     */
    notifyUpdated(payload: UppKonvaShapeEventPayload = {}): void {
        if (this._destroyed) {
            return;
        }
        this._onUpdated$.next(payload);
    }

    /**
     * Called by {@link UppKonvaArea.removeShapes} immediately before detaching and destroying this shape.
     *
     * @internal
     */
    notifyRemoved(): void {
        if (this._destroyed) {
            return;
        }
        this._onRemoved$.next();
    }

    // --------------------------------------------------------------------------
    // 5. Private members
    // --------------------------------------------------------------------------

    private _destroyed = false;
    private _konvaStage: UppKonvaStage | null = null;
    private _parentArea: UppKonvaArea | null = null;
}
